import { useEffect, useState, type FormEvent } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { AppFooter } from "../components/AppFooter";
import type { Invite } from "../types";

export function Pair() {
  const { user } = useAuth();
  const { profile } = useCouple();
  const [incoming, setIncoming] = useState<Invite[]>([]);
  const [outgoing, setOutgoing] = useState<Invite[]>([]);
  const [search, setSearch] = useState("");
  const [found, setFound] = useState<{ uid: string; username: string; displayName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const qIn = query(collection(db, "invites"), where("toUid", "==", user.uid), where("status", "==", "pending"));
    const unsub = onSnapshot(qIn, (snap) => setIncoming(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Invite)));
    return unsub;
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const qOut = query(collection(db, "invites"), where("fromUid", "==", user.uid), where("status", "==", "pending"));
    const unsub = onSnapshot(qOut, (snap) => setOutgoing(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Invite)));
    return unsub;
  }, [user]);

  async function handleSearch(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setFound(null);
    const usernameLower = search.trim().toLowerCase();
    if (!usernameLower) return;
    if (user && usernameLower === profile?.usernameLower) {
      setError("Das bist du selbst.");
      return;
    }
    setBusy(true);
    try {
      const nameSnap = await getDoc(doc(db, "usernames", usernameLower));
      if (!nameSnap.exists()) {
        setError("Diesen Benutzernamen gibt es nicht.");
        return;
      }
      const targetUid = nameSnap.data().uid as string;
      const userSnap = await getDoc(doc(db, "users", targetUid));
      if (!userSnap.exists()) {
        setError("Dieses Profil konnte nicht gefunden werden.");
        return;
      }
      setFound({ uid: targetUid, username: userSnap.data().username, displayName: userSnap.data().displayName });
    } catch {
      setError("Suche ist fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite() {
    if (!user || !found || !profile) return;
    setBusy(true);
    setError(null);
    try {
      const inviteRef = doc(collection(db, "invites"));
      await setDoc(inviteRef, {
        fromUid: user.uid,
        fromUsername: profile.username,
        fromDisplayName: profile.displayName,
        toUid: found.uid,
        toUsername: found.username,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      setInfo(`Einladung an ${found.displayName} gesendet.`);
      setFound(null);
      setSearch("");
    } catch {
      setError("Einladung konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelInvite(inviteId: string) {
    setBusy(true);
    try {
      await updateDoc(doc(db, "invites", inviteId), { status: "cancelled" });
    } finally {
      setBusy(false);
    }
  }

  async function declineInvite(inviteId: string) {
    setBusy(true);
    try {
      await updateDoc(doc(db, "invites", inviteId), { status: "declined" });
    } finally {
      setBusy(false);
    }
  }

  async function acceptInvite(invite: Invite) {
    if (!user || !profile) return;
    setBusy(true);
    setError(null);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "invites", invite.id), { status: "accepted" });
      batch.set(doc(db, "couples", invite.id), {
        memberUids: [invite.fromUid, invite.toUid],
        memberNames: {
          [invite.fromUid]: invite.fromDisplayName,
          [invite.toUid]: profile.displayName,
        },
        createdAt: serverTimestamp(),
      });
      await batch.commit();
    } catch {
      setError("Verbinden hat nicht geklappt. Bitte erneut versuchen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-16 pt-10">
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-plum-100 text-plum-700 dark:bg-night-surface-raised dark:text-night-accent">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
            <path d="M12 21s-7.5-4.6-10.3-9.3C.1 9.1 1 5.6 4 4.3c2.3-1 4.7-.1 6 1.8l2 2.9 2-2.9c1.3-1.9 3.7-2.8 6-1.8 3 1.3 3.9 4.8 2.3 7.4C19.5 16.4 12 21 12 21Z" />
          </svg>
        </div>
        <h1 className="mt-4 font-display text-2xl font-semibold text-plum-900 dark:text-night-accent">Verbinde dich</h1>
        <p className="mt-1.5 text-sm text-ink-600 dark:text-night-muted">
          Sucht euch gegenseitig per Benutzername, um eure Konten als Paar zusammenzuführen.
        </p>
      </div>

      {incoming.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Eingehende Einladungen</h2>
          <ul className="mt-2 space-y-2">
            {incoming.map((inv) => (
              <li key={inv.id} className="card flex items-center justify-between gap-3 rounded-2xl p-4">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink-900 dark:text-night-text">{inv.fromDisplayName}</p>
                  <p className="truncate text-xs text-ink-400 dark:text-night-faint">@{inv.fromUsername}</p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <button
                    disabled={busy}
                    onClick={() => declineInvite(inv.id)}
                    className="h-9 rounded-lg px-3 text-sm font-medium text-ink-400 dark:text-night-faint"
                  >
                    Ablehnen
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => acceptInvite(inv)}
                    className="h-9 rounded-lg bg-plum-700 px-3 text-sm font-semibold text-cream-50 disabled:opacity-60"
                  >
                    Annehmen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {outgoing.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Gesendete Einladung</h2>
          <ul className="mt-2 space-y-2">
            {outgoing.map((inv) => (
              <li key={inv.id} className="card flex items-center justify-between gap-3 rounded-2xl p-4">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink-900 dark:text-night-text">@{inv.toUsername}</p>
                  <p className="truncate text-xs text-ink-400 dark:text-night-faint">Warte auf Bestätigung…</p>
                </div>
                <button
                  disabled={busy}
                  onClick={() => cancelInvite(inv.id)}
                  className="h-9 flex-shrink-0 rounded-lg px-3 text-sm font-medium text-garnet-600"
                >
                  Zurückziehen
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Partner/-in einladen</h2>
        <form onSubmit={handleSearch} className="mt-2 flex gap-2">
          <input
            type="text"
            placeholder="Benutzername"
            autoCapitalize="off"
            autoCorrect="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <button
            type="submit"
            disabled={busy || !search.trim()}
            className="h-12 flex-shrink-0 rounded-xl bg-plum-700 px-4 text-sm font-semibold text-cream-50 disabled:opacity-60"
          >
            Suchen
          </button>
        </form>

        {found && (
          <div className="card mt-3 flex items-center justify-between gap-3 rounded-2xl p-4">
            <div className="min-w-0">
              <p className="truncate font-semibold text-ink-900 dark:text-night-text">{found.displayName}</p>
              <p className="truncate text-xs text-ink-400 dark:text-night-faint">@{found.username}</p>
            </div>
            <button
              disabled={busy}
              onClick={sendInvite}
              className="h-9 flex-shrink-0 rounded-lg bg-plum-700 px-3 text-sm font-semibold text-cream-50 disabled:opacity-60"
            >
              Einladen
            </button>
          </div>
        )}

        {info && <p className="mt-3 rounded-lg bg-plum-100 px-3 py-2 text-sm text-plum-800 dark:bg-night-surface-raised dark:text-night-accent">{info}</p>}
        {error && <p role="alert" className="mt-3 rounded-lg bg-garnet-100 px-3 py-2 text-sm text-garnet-600 dark:bg-garnet-600/15">{error}</p>}
      </section>

      <AppFooter />
    </main>
  );
}
